import React from 'react';

export const cn = (...classes) => classes.filter(Boolean).join(' ');

const focusRing = 'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-giggs-green/15';

const BUTTON_VARIANTS = {
  primary:
    'border-transparent bg-giggs-green text-white shadow-[0_6px_16px_rgba(32,150,104,0.20)] hover:-translate-y-0.5 hover:bg-giggs-green-hover hover:shadow-[0_10px_24px_rgba(32,150,104,0.28)] active:translate-y-0 active:shadow-[0_4px_12px_rgba(32,150,104,0.18)]',
  secondary:
    'border-transparent bg-[#EAF1ED] text-giggs-ink shadow-sm hover:-translate-y-0.5 hover:bg-[#DFEAE4] hover:shadow-md active:translate-y-0',
  outline:
    'border-transparent bg-white text-giggs-text shadow-sm ring-1 ring-inset ring-slate-200 hover:-translate-y-0.5 hover:text-giggs-ink hover:ring-giggs-green/40 hover:shadow-md active:translate-y-0',
  ghost:
    'border-transparent bg-transparent text-giggs-text hover:bg-white hover:text-giggs-ink hover:shadow-sm',
  danger:
    'border-transparent bg-red-600 text-white shadow-[0_6px_16px_rgba(220,38,38,0.18)] hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-[0_10px_22px_rgba(220,38,38,0.24)] active:translate-y-0',
  link:
    'border-transparent bg-transparent p-0 text-giggs-green hover:text-giggs-green-hover hover:underline',
};

const BUTTON_SIZES = {
  sm: 'min-h-[34px] px-3 py-1.5 text-xs',
  md: 'min-h-[42px] px-4 py-2.5 text-sm',
  lg: 'min-h-[48px] px-5 py-3 text-sm',
  icon: 'h-10 w-10 p-0',
};

export const Button = React.forwardRef(function Button(
  { className = '', variant = 'primary', size = 'md', type = 'button', children, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex   cursor-pointer items-center justify-center gap-2 rounded-xl border font-dm font-semibold transition-all duration-200 ease-out disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-50 disabled:shadow-none',
        focusRing,
        BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
        BUTTON_SIZES[size] || BUTTON_SIZES.md,
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});

export const inputClassName = cn(
  'box-border block w-full   rounded-xl border-0 bg-white px-3.5 py-3 font-dm text-sm font-normal text-giggs-ink shadow-[0_2px_8px_rgba(15,23,42,0.04)] ring-1 ring-inset ring-slate-200',
  'transition-all duration-200 ease-out placeholder:font-normal placeholder:text-slate-400',
  'hover:ring-slate-300 hover:shadow-[0_4px_12px_rgba(15,23,42,0.06)]',
  'focus:bg-white focus:outline-none focus:ring-2 focus:ring-giggs-green focus:shadow-[0_0_0_4px_rgba(32,150,104,0.08),0_6px_18px_rgba(15,23,42,0.07)]',
  'disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-giggs-muted disabled:opacity-70 disabled:shadow-none'
);

export const Input = React.forwardRef(function Input({ className = '', ...props }, ref) {
  return <input ref={ref} className={cn(inputClassName, className)} {...props} />;
});

export const Select = React.forwardRef(function Select({ className = '', children, ...props }, ref) {
  return (
    <select ref={ref} className={cn(inputClassName, 'cursor-pointer pr-10', className)} {...props}>
      {children}
    </select>
  );
});

export const Textarea = React.forwardRef(function Textarea({ className = '', ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(inputClassName, 'min-h-[104px] resize-y leading-6', className)}
      {...props}
    />
  );
});

export const Field = ({ label, htmlFor, hint, error, required, className = '', children }) => (
  <div className={cn('flex min-w-0 flex-col gap-2', className)}>
    {label && (
      <label htmlFor={htmlFor} className="font-dm text-xs font-semibold text-giggs-text">
        {label}{required ? <span className="text-red-500"> *</span> : ''}
      </label>
    )}
    {children}
    {error ? (
      <p className="m-0 font-dm text-xs font-normal text-red-600">{error}</p>
    ) : hint ? (
      <p className="m-0 font-dm text-xs leading-5 text-giggs-muted">{hint}</p>
    ) : null}
  </div>
);

export const Card = ({ className = '', children, ...props }) => (
  <section
    className={cn(
      'rounded-2xl bg-white shadow-[0_10px_34px_rgba(15,23,42,0.055)] ring-1 ring-black/[0.045] transition-all duration-200',
      className
    )}
    {...props}
  >
    {children}
  </section>
);

export const CardHeader = ({ title, description, action, className = '', children }) => (
  <div className={cn('flex flex-wrap items-center justify-between gap-4 px-5 pb-4 pt-5 sm:px-6 sm:pt-6', className)}>
    <div className="min-w-0">
      {title && <h2 className="m-0 font-nunito text-base font-bold text-giggs-ink sm:text-lg">{title}</h2>}
      {description && <p className="mb-0 mt-1 font-dm text-xs font-normal leading-5 text-giggs-muted sm:text-sm">{description}</p>}
      {children}
    </div>
    {action}
  </div>
);

export const CardBody = ({ className = '', children }) => (
  <div className={cn('px-5 pb-5 sm:px-6 sm:pb-6', className)}>{children}</div>
);

export const CardFooter = ({ className = '', children }) => (
  <div className={cn('flex flex-wrap items-center justify-end gap-3 rounded-b-2xl bg-slate-50/70 px-5 py-4 sm:px-6', className)}>
    {children}
  </div>
);

const ALERT_VARIANTS = {
  info: 'bg-blue-50 text-blue-800 ring-blue-100',
  success: 'bg-emerald-50 text-emerald-800 ring-emerald-100',
  warning: 'bg-amber-50 text-amber-800 ring-amber-100',
  danger: 'bg-red-50 text-red-700 ring-red-100',
};

export const Alert = ({ variant = 'info', title, className = '', children }) => (
  <div className={cn('rounded-xl px-4 py-3 font-dm text-sm leading-5 ring-1 ring-inset', ALERT_VARIANTS[variant] || ALERT_VARIANTS.info, className)}>
    {title && <strong className="mr-1 font-semibold">{title}</strong>}
    {children}
  </div>
);

const BADGE_VARIANTS = {
  neutral: 'bg-slate-100 text-slate-600',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  warning: 'bg-amber-50 text-amber-700 ring-amber-100',
  danger: 'bg-red-50 text-red-700 ring-red-100',
  info: 'bg-blue-50 text-blue-700 ring-blue-100',
  green: 'bg-giggs-green/10 text-[#177552] ring-giggs-green/10',
};

export const Badge = ({ variant = 'neutral', className = '', children }) => (
  <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 font-dm text-[11px] font-semibold leading-none ring-1 ring-inset ring-transparent', BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral, className)}>
    {children}
  </span>
);

export const Tabs = ({ className = '', children }) => (
  <div className={cn('inline-flex max-w-full flex-wrap gap-1.5 rounded-2xl bg-slate-100/80 p-1.5', className)}>{children}</div>
);

export const TabButton = ({ active, count, className = '', children, ...props }) => (
  <button
    type="button"
    className={cn(
      'inline-flex min-h-[36px]   cursor-pointer items-center gap-2 rounded-xl border-0 px-3.5 py-2 font-dm text-xs font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-giggs-green/10 sm:text-sm',
      active
        ? 'bg-white text-giggs-ink shadow-sm ring-1 ring-black/[0.04]'
        : 'bg-transparent text-giggs-muted hover:bg-white/70 hover:text-giggs-ink',
      className
    )}
    {...props}
  >
    {children}
    {count !== undefined && count !== null && (
      <span className={cn('inline-flex min-w-[19px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold', active ? 'bg-giggs-green/10 text-giggs-green' : 'bg-slate-200/80 text-slate-500')}>
        {count}
      </span>
    )}
  </button>
);

export const Toolbar = ({ className = '', children }) => (
  <div className={cn('mb-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between', className)}>
    {children}
  </div>
);

export const TableShell = ({ className = '', children }) => (
  <div className={cn('overflow-hidden rounded-2xl bg-white shadow-[0_10px_34px_rgba(15,23,42,0.05)] ring-1 ring-black/[0.045]', className)}>
    <div className="w-full overflow-x-auto">{children}</div>
  </div>
);

export const tableClassName = 'w-full min-w-[680px] border-collapse font-dm text-sm font-normal';
export const thClassName = 'whitespace-nowrap bg-[#F8FAF9] px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-[#7C8D84]';
export const tdClassName = 'border-t border-slate-100 px-4 py-4 align-middle font-normal text-sm text-[#2A3A33]';

export const EmptyState = ({ title, description, className = '', action }) => (
  <div className={cn('px-5 py-14 text-center font-dm text-sm font-normal text-giggs-muted', className)}>
    {title && <strong className="block font-nunito text-base font-bold text-giggs-ink">{title}</strong>}
    {description && <p className="mx-auto mb-0 mt-1 max-w-lg leading-6">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Spinner = ({ size = 18, className = '' }) => (
  <span
    className={cn('inline-block animate-spin rounded-full border-2 border-current border-t-transparent', className)}
    style={{ width: size, height: size }}
    aria-hidden="true"
  />
);

export const Toast = ({ children, className = '' }) => (
  <div className={cn('mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 font-dm text-sm font-semibold text-emerald-700 shadow-sm ring-1 ring-inset ring-emerald-100', className)}>
    {children}
  </div>
);

export const PageHeader = ({ title, description, action, className = '' }) => (
  <div className={cn('mb-6 flex flex-wrap items-end justify-between gap-4', className)}>
    <div className="min-w-0">
      <h1 className="m-0 font-nunito text-[22px] font-extrabold text-giggs-ink sm:text-2xl">{title}</h1>
      {description && <p className="mb-0 mt-1 font-dm text-[13px] font-normal leading-5 text-giggs-muted sm:text-sm">{description}</p>}
    </div>
    {action}
  </div>
);

export const StatCard = ({ icon, label, value, tone = 'green', className = '' }) => {
  const tones = {
    green: 'bg-emerald-50 text-[#177552]',
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
    neutral: 'bg-slate-100 text-slate-600',
  };
  return (
    <Card className={cn('group flex min-w-0 items-center gap-4 p-5 hover:-translate-y-0.5 hover:shadow-[0_16px_38px_rgba(15,23,42,0.08)]', className)}>
      {icon && <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-transform duration-200 group-hover:scale-105', tones[tone] || tones.green)}>{icon}</div>}
      <div className="min-w-0">
        <div className="font-dm text-xs font-semibold text-giggs-muted">{label}</div>
        <div className="mt-1 font-nunito text-[23px] font-extrabold leading-none text-giggs-ink">{value}</div>
      </div>
    </Card>
  );
};
